import * as environment from "@shared/environment.js";

function getWordVariations(word) {
        
    let baseWord = word.toLowerCase();
    let variations = new Set();
    
    // Special case, don't expand "re" to "red"
    if (baseWord === "re") {
        return variations;
    }

    // Add the base word
    variations.add(baseWord);
    // Handling regular plurals and verb forms
    if (baseWord.endsWith("s")) {
        // Singular form for regular plurals
        variations.add(baseWord.slice(0, -1));
    } else {
        // Regular plural
        variations.add(baseWord + "s");
    }
    if (baseWord.endsWith("e")) {
        // Adding "d" for past tense (e.g., "like" → "liked")
        variations.add(baseWord + "d");
        // Adding "ing" for present participle (e.g., "like" → "liking")
        variations.add(baseWord.slice(0, -1) + "ing");
    } else {
        // Adding "ed" for past tense (e.g., "walk" → "walked")
        variations.add(baseWord + "ed");
        // Adding "ing" for present participle (e.g., "walk" → "walking")
        variations.add(baseWord + "ing");
    }
    // Handling "tion" (convert to "tion" for words ending in "ate")
    if (baseWord.endsWith("ate")) {
        variations.add(baseWord.slice(0, -3) + "tion");
    }
    // Handling "ism" (convert to "istic" for adjectives, "ist" for practitioners)
    if (baseWord.endsWith("ism")) {
        variations.add(baseWord.slice(0, -3) + "istic"); // Adjective form
        variations.add(baseWord.slice(0, -3) + "ist"); // Practitioner form
    }
    return Array.from(variations);
}

function randomString(length = 12) {
    const charset = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    const values = new Uint8Array(length);
    window.crypto.getRandomValues(values);
    return [...values].map(value => charset[value % charset.length]).join("");
}

function capitalize(str) {
    if (str.length === 0) {
        return str;
    }
    return str.charAt(0).toUpperCase() + str.slice(1);
}

function swapItems(idx1, idx2, array) {
    if (idx1 >= 0 && idx1 < array.length && idx2 >= 0 && idx2 < array.length && idx1 != idx2) {
        let temp = array[idx1];
        array[idx1] = array[idx2];
        array[idx2] = temp;
    }
}

function sortByModDate(feedItems) {
    feedItems.sort((a, b) => {
        // Handle cases where last_modified is undefined
        if (!a.last_modified) { return 1 }; // Move "a" towards the end if last_modified is missing
        if (!b.last_modified) { return -1 }; // Move "b" towards the end if last_modified is missing

        // Convert ISO 8601 date strings to Date objects for comparison
        const dateA = new Date(a.last_modified);
        const dateB = new Date(b.last_modified);

        // Sort in reverse chronological order
        return dateB - dateA;
    });
}

function sortByPopularity(feedItems, countGetter) {
    feedItems.sort((a, b) => {
        let aCnt = countGetter(a.id);
        let bCnt = countGetter(b.id);
        return bCnt - aCnt;
    });
}

function getWidth(element) {
    let clonedEl = element.cloneNode(true);
    clonedEl.style.visibility = "hidden";
    document.body.appendChild(clonedEl);
    var width = clonedEl.offsetWidth;
    document.body.removeChild(clonedEl);
    return width;
}

function getHeight(element) {
    let clonedEl = element.cloneNode(true);
    clonedEl.style.visibility = "hidden";
    document.body.appendChild(clonedEl);
    var height = clonedEl.offsetHeight;
    document.body.removeChild(clonedEl);
    return height;
}

// Useful for removing listeners from the target element
function cloneAndReplace(element) {
    var newEl = element.cloneNode(true);
    element.parentNode.replaceChild(newEl, element);
}

function sleep(seconds) {
    return new Promise(resolve => {
        setTimeout(resolve, seconds * 1000);
    });
}

function debugDesc(obj) {
    if (!obj) {
        return "undefined";
    }

    return JSON.stringify(obj, (key, value) => {
        return value instanceof Element ? value.outerHTML : value;
    }, 2);
}

function copy(obj) {
    if (typeof structuredClone === "function") {
        return structuredClone(obj);
    } else {
        return JSON.parse(JSON.stringify(obj));
    }
}

function isLocalStorageDebugLoggingEnabled() {
    try {
        return Boolean(window.localStorage) && window.localStorage.getItem("cyoKitDebugLogging") === "true";
    } catch (_error) {
        return false;
    }
}

function dlog(message, category) {
    if (environment.shouldDLog() || window.debugLogging || isLocalStorageDebugLoggingEnabled()) {
        let categoryStr = category ? `[${category}] ` : " ";
        console.log(`[cyo-kit]${categoryStr}${message}`);
    }
}

function perfLog(message) {
    dlog(message, "performance");
}

function dLogTime(label) {
    if (environment.shouldDLog() || window.debugLogging) {
        console.time(`[cyo-kit] ${label}`);
    }
}

function dLogTimeEnd(label) {
    if (environment.shouldDLog() || window.debugLogging) {
        console.timeEnd(`[cyo-kit] ${label}`);
    }    
}

function assert(condition, message = "Assertion failed") {
    if (!condition) {
        throw new Error(message);
    }
}

function isStickerSet(item) {
    return item && item.id && item.id.includes("sticker-set");
}

function isMobileDevice() {
    const hasTouch = 'ontouchstart' in window;
    const hasCoarsePointer = window.matchMedia('(pointer: coarse)').matches;
    const hasFinePointer = window.matchMedia('(pointer: fine)').matches;
    
    return hasTouch && hasCoarsePointer && !hasFinePointer;
}

function downloadCanvasForTesting(canvas, filename = "cyo-test-image.png") {
    const link = document.createElement("a");
    link.download = filename;
    link.href = canvas.toDataURL("image/png");
    link.click();
}

function downloadImageForTesting(image, filename = "cyo-test-image.png") {
    const canvas = document.createElement("canvas");
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(image, 0, 0);
    
    downloadCanvasForTesting(canvas, filename);
}

function logCanvasMemory(canvas, label = "") {
    if (!(canvas instanceof HTMLCanvasElement)) {
        return;
    }

    if (environment.shouldDLog() || window.debugLogging) {
        const dpr = window.devicePixelRatio || 1;
        const pixelCount = canvas.width * canvas.height;
        const approxMB = (pixelCount * 4) / (1024 * 1024);
        const labelSuffix = label ? ` ${label}` : "";
    
        console.log(`[cyo-kit][performance] canvas${labelSuffix} ${canvas.width}×${canvas.height} @dpr=${dpr} ~${approxMB.toFixed(1)} MB/buffer`);
    }
}

// These use to be array prototype extensions but ran into conflicts so now we have this.
const ArrayUtil = {
    top: arr => arr[0],
    right: arr => arr[1],
    bottom: arr => arr[2],
    left: arr => arr[3],
    x: arr => arr[0],
    y: arr => arr[1]
};

function isIdentityTransform(transform) {
    if (!transform) return true;
    return transform.scale === 1 &&
           transform.rotate === 0 &&
           transform.translateX === 0 &&
           transform.translateY === 0;
}

function setClarityTag(tagName, value = "true") {
    if (typeof window.clarity === "function") {
        window.clarity("set", tagName, value);
    }
}

// Safari <=14 has a bug where styles injected via innerHTML into Shadow DOM
// aren't parsed on hard reload. Recreating the style element forces Safari to parse it.
// The user may see a momentary flash of missing styles while this happens.
function safariShadowDomStyleFix(shadowRoot) {
    const safariMatch = navigator.userAgent.match(/Version\/(\d+)\..*Safari/);
    const safariVersion = safariMatch ? parseInt(safariMatch[1], 10) : null;
    if (safariVersion !== null && safariVersion <= 14) {
        setTimeout(() => {
            const style = shadowRoot.querySelector("style");
            if (style) {
                dlog(`Safari <=14 Shadow DOM style fix applied to <${shadowRoot.host.tagName.toLowerCase()}>`);
                const css = style.textContent;
                style.remove();
                const newStyle = document.createElement("style");
                newStyle.textContent = css;
                shadowRoot.insertBefore(newStyle, shadowRoot.firstChild);
            }
        }, 0);
    }
}

// Used to work around lack of 'gap' support in older browsers. Fallback is typically to use margins on child elements.
let _flexGapSupported = null;
function supportsFlexGap() {
    if (_flexGapSupported === null) {
        // aspect-ratio was added after flexbox gap, so if it's supported, gap definitely is too
        if (CSS.supports("aspect-ratio", "1")) {
            _flexGapSupported = true;
        } else {
            // For older browsers, test if gap actually renders in flexbox
            const flex = document.createElement("div");
            flex.style.display = "flex";
            flex.style.flexDirection = "column";
            flex.style.gap = "1px";
            flex.appendChild(document.createElement("div"));
            flex.appendChild(document.createElement("div"));
            document.body.appendChild(flex);
            _flexGapSupported = flex.scrollHeight === 1;
            document.body.removeChild(flex);
        }
    }
    return _flexGapSupported;
}

export { getWordVariations, randomString, capitalize, swapItems, sortByModDate, sortByPopularity, getWidth, getHeight, sleep, cloneAndReplace, debugDesc, copy, dlog, perfLog, dLogTime, dLogTimeEnd, assert, isStickerSet, isMobileDevice, downloadCanvasForTesting, downloadImageForTesting, logCanvasMemory, ArrayUtil, isIdentityTransform, safariShadowDomStyleFix, supportsFlexGap, setClarityTag };
